import { Dialog } from '@angular/cdk/dialog'
import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { DialogService } from '../../services/dialog.service'
import { ErrorService } from '../../services/error.service'
import { SpinnerService } from '../../services/spinner.service'
import { WindowService } from '../../services/window.service'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import { MatSelectModule } from '@angular/material/select'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { CommonModule } from '@angular/common'
import { SubstanceListComponent } from './substance-list/substance-list.component'
import {
  Regulation,
  RegulationSubstancesResult,
  Substance,
  SubstanceListFilter,
} from '../../types/chemistry'
import { RegulationService } from '../../services/regulation.service'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { ApiError } from '../../types/apiError'
import { RouteService } from '../../services/route.service'
import { ROUTES } from '../../constants/routes'
import { PaginatorIntl } from '../intl/paginator-intl'
import { TranslationService } from '../../services/translation.service'

@Component({
  selector: 'app-substances',
  imports: [
    CommonModule,
    MatInputModule,
    MatFormField,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    FormsModule,
    MatIcon,
    SubstanceListComponent,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntl,
    },
  ],
  templateUrl: './substances.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './substances.component.scss',
})
export class SubstancesComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator

  dialog = inject(Dialog)
  dialogService = inject(DialogService)
  errorService = inject(ErrorService)
  spinnerService = inject(SpinnerService)
  windowService = inject(WindowService)
  regulationService = inject(RegulationService)
  routeService = inject(RouteService)
  formBuilder = inject(FormBuilder)
  translationService = inject(TranslationService)

  filterForm = this.formBuilder.group({
    regulationId: [null, Validators.required],
    name: [null],
    casNumber: [null],
  })

  isMobileView: boolean = false
  page: number = 1

  activeFilter: SubstanceListFilter = {
    regulationId: null,
    name: null,
    casNumber: null,
  }

  regulations: Array<Regulation> = []
  regulationSubstanceResults: RegulationSubstancesResult = {
    totalRecords: 0,
    results: [],
  }
  displayedColumns: Array<string> = ['name', 'casNumber']
  dataSource = new MatTableDataSource<Substance>(
    this.regulationSubstanceResults.results,
  )

  async ngOnInit(): Promise<void> {
    try {
      this.spinnerService.startSpinner()
      this.windowService.isMobileView$.subscribe((isMobileView) => {
        this.isMobileView = isMobileView
      })

      const currentRoute = this.routeService.getUserRoutes(
        ROUTES.VITE_MARKETPLACE.title,
        ROUTES.SUBSTANCES.path,
      )
      this.routeService.setRoute(currentRoute)

      const regulationsResponse = await firstValueFrom(
        this.regulationService.getAll(),
      )
      this.regulations = regulationsResponse

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  ngAfterViewInit(): void {
    // Assigns the paginator to table dataSource
    this.dataSource.paginator = this.paginator
  }

  /**
   * Handles the page change events from the paginator and gets the new page data from the API
   * @param {PageEvent} event Event containing new page
   */
  async changePage(event: PageEvent) {
    this.page = event.pageIndex + 1
    await this.getRegulationSubstances()
  }

  /**
   * Makes request to get substance regulations
   */
  async getRegulationSubstances() {
    try {
      if (!this.filterForm.valid) {
        this.filterForm.markAllAsTouched()
        return
      }
      this.spinnerService.startSpinner()
      const filter = this.filterForm.value as SubstanceListFilter

      this.regulationSubstanceResults = await firstValueFrom(
        this.regulationService.getRegulationSubstances(this.page, filter),
      )

      this.onFilterChange()

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets page index on filter change
   */
  onFilterChange() {
    const filter = this.filterForm.value as SubstanceListFilter
    for (const key of Object.keys(filter)) {
      let resetPage = false
      if (typeof key === 'string') {
        if (
          filter[key as keyof SubstanceListFilter]?.toString().toLowerCase() !=
          this.activeFilter[key as keyof SubstanceListFilter]
            ?.toString()
            .toLowerCase()
        ) {
          resetPage = true
        }
      } else if (
        filter[key as keyof SubstanceListFilter] !=
        this.activeFilter[key as keyof SubstanceListFilter]
      ) {
        resetPage = true
      }

      if (resetPage) {
        this.activeFilter = JSON.parse(JSON.stringify(filter))
        this.page = 1
        if (this.paginator != null) {
          this.paginator.pageIndex = 0
        }
      }
    }
  }

  /**
   * Resets the filter values
   */
  resetFilter() {
    this.filterForm.reset()
  }
}
